/**
 * Anchor Browser API client for the UI
 * Communicates with our backend which uses Anchor Browser
 */

import type {
  CreateTaskRequest,
  CreateTaskResponse,
  Task,
  ListTasksResponse,
  ListTasksParams,
  TaskMedia,
  AnchorBrowserClientConfig,
  ScheduledTask,
  CreateScheduledTaskRequest,
  UpdateScheduledTaskRequest,
  ListScheduledTasksResponse,
  ListScheduledTasksParams,
  BrowserSession,
  CreateSessionRequest
} from '../types/anchor-browser'

// Global variable to store the session token getter function
let getSessionToken: (() => Promise<string | null>) | null = null

// Function to set the session token getter (called from components with Clerk context)
export function setSessionTokenGetter(getter: () => Promise<string | null>) {
  getSessionToken = getter
}

export class AnchorBrowserApiError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
    public readonly response?: any
  ) {
    super(message)
    this.name = 'AnchorBrowserApiError'
  }
}

class AnchorBrowserApiClient {
  private baseUrl: string
  private timeout: number

  constructor(config: AnchorBrowserClientConfig = {}) {
    // Use the same logic as the main API client for base URL
    const isProductionDomain = typeof window !== 'undefined' && window.location.hostname === 'app.smokestack.dev'
    const defaultBaseUrl = isProductionDomain
      ? 'https://api.smokestack.dev/api'
      : typeof window !== 'undefined' && import.meta.env?.DEV
        ? '/api'
        : 'https://api.smokestack.dev/api'

    this.baseUrl = config.baseUrl || defaultBaseUrl
    this.timeout = config.timeout || 120000 // 2 minutes for Anchor Browser tasks
  }



  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    expectBlob: boolean = false
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    console.log('AnchorBrowserApi.request:', { url, method: options.method || 'GET' })

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.timeout)

    // Get Clerk session token for authentication
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    }

    if (getSessionToken) {
      const token = await getSessionToken()
      console.log('Got session token:', token ? 'yes' : 'no')
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
    }

    console.log('Request headers:', headers)
    console.log('Request body:', options.body)

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers,
        credentials: 'include',
      })

      console.log('Response status:', response.status)
      console.log('Response headers:', Object.fromEntries(response.headers.entries()))

      clearTimeout(timeoutId)

      if (!response.ok) {
        console.log('Response not ok, status:', response.status)
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`
        let errorResponse: any = null

        try {
          errorResponse = await response.json()
          console.log('Error response body:', errorResponse)
          if (errorResponse.error) {
            errorMessage = errorResponse.error
          }
        } catch {
          // Response is not JSON, use status text
          console.log('Could not parse error response, using status text:', errorMessage)
        }

        console.log('Throwing AnchorBrowserApiError:', errorMessage)
        throw new AnchorBrowserApiError(errorMessage, response.status, errorResponse)
      }

      if (expectBlob) {
        const responseData = await response.blob()
        console.log('Response blob size:', responseData.size)
        return responseData as T
      } else {
        const responseData = await response.json()
        console.log('Response data:', responseData)
        return responseData
      }
    } catch (error) {
      clearTimeout(timeoutId)
      
      if (error instanceof AnchorBrowserApiError) {
        throw error
      }
      
      if (error instanceof Error && error.name === 'AbortError') {
        throw new AnchorBrowserApiError('Request timeout')
      }
      
      throw new AnchorBrowserApiError(
        error instanceof Error ? error.message : 'Unknown error occurred'
      )
    }
  }

  // Task management
  createTask = async (request: CreateTaskRequest): Promise<CreateTaskResponse> => {
    console.log('AnchorBrowserApi.createTask called with:', request)
    try {
      const result = await this.request<CreateTaskResponse>('/browser-tasks', {
        method: 'POST',
        body: JSON.stringify(request),
      })
      console.log('AnchorBrowserApi.createTask success:', result)
      return result
    } catch (error) {
      console.error('AnchorBrowserApi.createTask error:', error)
      throw error
    }
  }

  // ===== NEW CORRECT ANCHOR BROWSER APIs =====

  // Navigate to URL using session-specific endpoint
  navigateToUrl = async (sessionId: string, url: string): Promise<{status: string}> => {
    console.log(`AnchorBrowserApi.navigateToUrl called: ${sessionId} -> ${url}`)
    try {
      const result = await this.request<{status: string}>(`/sessions/${sessionId}/goto`, {
        method: 'POST',
        body: JSON.stringify({ url }),
      })
      console.log('AnchorBrowserApi.navigateToUrl success:', result)
      return result
    } catch (error) {
      console.error('AnchorBrowserApi.navigateToUrl error:', error)
      throw error
    }
  }

  // Perform AI web task using session-specific endpoint
  // Returns result plus optional prompt enhancement payload
  performWebTask = async (
    sessionId: string,
    prompt: string
  ): Promise<{ data: { result: string }; enhancement?: import('../types/anchor-browser').PromptEnhancementResult }> => {
    console.log(`AnchorBrowserApi.performWebTask called: ${sessionId} -> ${prompt}`)
    try {
      const result = await this.request<{ data: { result: string }; enhancement?: import('../types/anchor-browser').PromptEnhancementResult }>(`/tools/perform-web-task?sessionId=${sessionId}`, {
        method: 'POST',
        body: JSON.stringify({
          prompt: prompt,
          provider: 'openai',
          model: 'gpt-4o-mini',
        }),
      })
      console.log('AnchorBrowserApi.performWebTask success:', result)
      return result
    } catch (error) {
      console.error('AnchorBrowserApi.performWebTask error:', error)

      // Handle specific error codes
      if (error instanceof AnchorBrowserApiError) {
        if (error.response?.code === 'SESSION_EXPIRED') {
          throw new AnchorBrowserApiError('Session has expired. Please create a new session and try again.', 410, error.response)
        } else if (error.response?.code === 'SERVICE_UNAVAILABLE') {
          throw new AnchorBrowserApiError('Anchor Browser service is temporarily unavailable. Please try again in a moment.', 503, error.response)
        } else if (error.response?.code === 'TASK_EXECUTION_FAILED') {
          throw new AnchorBrowserApiError(`Task execution failed: ${error.response.details || error.message}`, error.status, error.response)
        }
      }

      throw error
    }
  }

  // ===== DIRECT ANCHOR BROWSER OS-LEVEL CONTROL APIs =====

  // Mouse Actions
  mouseClick = async (sessionId: string, params: { x?: number; y?: number; selector?: string; button?: 'left' | 'right' | 'middle' }): Promise<{status: string}> => {
    console.log(`AnchorBrowserApi.mouseClick: ${sessionId}`, params)
    try {
      const result = await this.request<{status: string}>(`/sessions/${sessionId}/click`, {
        method: 'POST',
        body: JSON.stringify(params),
      })
      console.log('AnchorBrowserApi.mouseClick success:', result)
      return result
    } catch (error) {
      console.error('AnchorBrowserApi.mouseClick error:', error)
      throw error
    }
  }

  mouseDoubleClick = async (sessionId: string, params: { x?: number; y?: number; selector?: string }): Promise<{status: string}> => {
    console.log(`AnchorBrowserApi.mouseDoubleClick: ${sessionId}`, params)
    try {
      const result = await this.request<{status: string}>(`/sessions/${sessionId}/double-click`, {
        method: 'POST',
        body: JSON.stringify(params),
      })
      console.log('AnchorBrowserApi.mouseDoubleClick success:', result)
      return result
    } catch (error) {
      console.error('AnchorBrowserApi.mouseDoubleClick error:', error)
      throw error
    }
  }

  mouseDown = async (sessionId: string, params: { x?: number; y?: number; selector?: string; button?: 'left' | 'right' | 'middle' }): Promise<{status: string}> => {
    console.log(`AnchorBrowserApi.mouseDown: ${sessionId}`, params)
    try {
      const result = await this.request<{status: string}>(`/sessions/${sessionId}/mouse-down`, {
        method: 'POST',
        body: JSON.stringify(params),
      })
      console.log('AnchorBrowserApi.mouseDown success:', result)
      return result
    } catch (error) {
      console.error('AnchorBrowserApi.mouseDown error:', error)
      throw error
    }
  }

  mouseUp = async (sessionId: string, params: { x?: number; y?: number; selector?: string; button?: 'left' | 'right' | 'middle' }): Promise<{status: string}> => {
    console.log(`AnchorBrowserApi.mouseUp: ${sessionId}`, params)
    try {
      const result = await this.request<{status: string}>(`/sessions/${sessionId}/mouse-up`, {
        method: 'POST',
        body: JSON.stringify(params),
      })
      console.log('AnchorBrowserApi.mouseUp success:', result)
      return result
    } catch (error) {
      console.error('AnchorBrowserApi.mouseUp error:', error)
      throw error
    }
  }

  mouseMove = async (sessionId: string, params: { x: number; y: number; selector?: string }): Promise<{status: string}> => {
    console.log(`AnchorBrowserApi.mouseMove: ${sessionId}`, params)
    try {
      const result = await this.request<{status: string}>(`/sessions/${sessionId}/mouse-move`, {
        method: 'POST',
        body: JSON.stringify(params),
      })
      console.log('AnchorBrowserApi.mouseMove success:', result)
      return result
    } catch (error) {
      console.error('AnchorBrowserApi.mouseMove error:', error)
      throw error
    }
  }

  dragDrop = async (sessionId: string, params: {
    startX: number; startY: number; endX: number; endY: number;
    startSelector?: string; endSelector?: string
  }): Promise<{status: string}> => {
    console.log(`AnchorBrowserApi.dragDrop: ${sessionId}`, params)
    try {
      const result = await this.request<{status: string}>(`/sessions/${sessionId}/drag-drop`, {
        method: 'POST',
        body: JSON.stringify(params),
      })
      console.log('AnchorBrowserApi.dragDrop success:', result)
      return result
    } catch (error) {
      console.error('AnchorBrowserApi.dragDrop error:', error)
      throw error
    }
  }

  // Scroll Action
  scroll = async (sessionId: string, params: {
    x?: number; y?: number; deltaX?: number; deltaY?: number; steps?: number
  }): Promise<{status: string}> => {
    console.log(`AnchorBrowserApi.scroll: ${sessionId}`, params)
    try {
      const result = await this.request<{status: string}>(`/sessions/${sessionId}/scroll`, {
        method: 'POST',
        body: JSON.stringify(params),
      })
      console.log('AnchorBrowserApi.scroll success:', result)
      return result
    } catch (error) {
      console.error('AnchorBrowserApi.scroll error:', error)
      throw error
    }
  }

  // Text Input Actions
  typeText = async (sessionId: string, params: { text: string; selector?: string }): Promise<{status: string}> => {
    console.log(`AnchorBrowserApi.typeText: ${sessionId}`, params)
    try {
      const result = await this.request<{status: string}>(`/sessions/${sessionId}/type`, {
        method: 'POST',
        body: JSON.stringify(params),
      })
      console.log('AnchorBrowserApi.typeText success:', result)
      return result
    } catch (error) {
      console.error('AnchorBrowserApi.typeText error:', error)
      throw error
    }
  }

  keyboardShortcut = async (sessionId: string, params: { keys: string[] }): Promise<{status: string}> => {
    console.log(`AnchorBrowserApi.keyboardShortcut: ${sessionId}`, params)
    try {
      const result = await this.request<{status: string}>(`/sessions/${sessionId}/keyboard-shortcut`, {
        method: 'POST',
        body: JSON.stringify(params),
      })
      console.log('AnchorBrowserApi.keyboardShortcut success:', result)
      return result
    } catch (error) {
      console.error('AnchorBrowserApi.keyboardShortcut error:', error)
      throw error
    }
  }

  // Clipboard Actions
  getClipboard = async (sessionId: string): Promise<{content: string}> => {
    console.log(`AnchorBrowserApi.getClipboard: ${sessionId}`)
    try {
      const result = await this.request<{content: string}>(`/sessions/${sessionId}/clipboard`, {
        method: 'GET',
      })
      console.log('AnchorBrowserApi.getClipboard success:', result)
      return result
    } catch (error) {
      console.error('AnchorBrowserApi.getClipboard error:', error)
      throw error
    }
  }

  setClipboard = async (sessionId: string, params: { content: string }): Promise<{status: string}> => {
    console.log(`AnchorBrowserApi.setClipboard: ${sessionId}`, params)
    try {
      const result = await this.request<{status: string}>(`/sessions/${sessionId}/clipboard`, {
        method: 'POST',
        body: JSON.stringify(params),
      })
      console.log('AnchorBrowserApi.setClipboard success:', result)
      return result
    } catch (error) {
      console.error('AnchorBrowserApi.setClipboard error:', error)
      throw error
    }
  }

  copySelectedText = async (sessionId: string): Promise<{status: string}> => {
    console.log(`AnchorBrowserApi.copySelectedText: ${sessionId}`)
    try {
      const result = await this.request<{status: string}>(`/sessions/${sessionId}/copy`, {
        method: 'POST',
      })
      console.log('AnchorBrowserApi.copySelectedText success:', result)
      return result
    } catch (error) {
      console.error('AnchorBrowserApi.copySelectedText error:', error)
      throw error
    }
  }

  pasteText = async (sessionId: string): Promise<{status: string}> => {
    console.log(`AnchorBrowserApi.pasteText: ${sessionId}`)
    try {
      const result = await this.request<{status: string}>(`/sessions/${sessionId}/paste`, {
        method: 'POST',
      })
      console.log('AnchorBrowserApi.pasteText success:', result)
      return result
    } catch (error) {
      console.error('AnchorBrowserApi.pasteText error:', error)
      throw error
    }
  }

  // Screenshot Action
  takeScreenshot = async (sessionId: string): Promise<Blob> => {
    console.log(`AnchorBrowserApi.takeScreenshot: ${sessionId}`)
    try {
      const result = await this.request<Blob>(`/sessions/${sessionId}/screenshot`, {
        method: 'GET',
      }, true) // true for blob response
      console.log('AnchorBrowserApi.takeScreenshot success')
      return result
    } catch (error) {
      console.error('AnchorBrowserApi.takeScreenshot error:', error)
      throw error
    }
  }





  getTask = async (taskId: string): Promise<Task> => {
    return this.request<Task>(`/browser-tasks/${taskId}`)
  }

  listTasks = async (params: ListTasksParams = {}): Promise<ListTasksResponse> => {
    const searchParams = new URLSearchParams()

    if (params.status) searchParams.append('status', params.status)
    if (params.limit) searchParams.append('limit', params.limit.toString())
    if (params.page) searchParams.append('page', params.page.toString())
    if (params.created_after) searchParams.append('created_after', params.created_after)
    if (params.created_before) searchParams.append('created_before', params.created_before)

    const queryString = searchParams.toString()
    const endpoint = `/browser-tasks${queryString ? `?${queryString}` : ''}`

    return this.request<ListTasksResponse>(endpoint)
  }

  stopTask = async (taskId: string): Promise<void> => {
    await this.request(`/browser-tasks/${taskId}/stop`, {
      method: 'PUT',
    })
  }

  pauseTask = async (taskId: string): Promise<void> => {
    await this.request(`/browser-tasks/${taskId}/pause`, {
      method: 'PUT',
    })
  }

  resumeTask = async (taskId: string): Promise<void> => {
    await this.request(`/browser-tasks/${taskId}/resume`, {
      method: 'PUT',
    })
  }

  getTaskMedia = async (taskId: string): Promise<TaskMedia> => {
    return this.request<TaskMedia>(`/browser-tasks/${taskId}/media`)
  }

  // Scheduled tasks (for future use)
  createScheduledTask = async (request: CreateScheduledTaskRequest): Promise<ScheduledTask> => {
    return this.request<ScheduledTask>('/scheduled-tasks', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  getScheduledTask = async (taskId: string): Promise<ScheduledTask> => {
    return this.request<ScheduledTask>(`/scheduled-tasks/${taskId}`)
  }

  updateScheduledTask = async (request: UpdateScheduledTaskRequest): Promise<ScheduledTask> => {
    return this.request<ScheduledTask>(`/scheduled-tasks/${request.id}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    })
  }

  deleteScheduledTask = async (taskId: string): Promise<void> => {
    await this.request(`/scheduled-tasks/${taskId}`, {
      method: 'DELETE',
    })
  }

  listScheduledTasks = async (params: ListScheduledTasksParams = {}): Promise<ListScheduledTasksResponse> => {
    const searchParams = new URLSearchParams()

    if (params.enabled !== undefined) searchParams.append('enabled', params.enabled.toString())
    if (params.limit) searchParams.append('limit', params.limit.toString())
    if (params.offset) searchParams.append('offset', params.offset.toString())

    const queryString = searchParams.toString()
    const endpoint = `/scheduled-tasks${queryString ? `?${queryString}` : ''}`

    return this.request<ListScheduledTasksResponse>(endpoint)
  }

  // Session management
  sessions = {
    listSessions: async (): Promise<{ sessions: BrowserSession[]; total: number }> => {
      return this.request<{ sessions: BrowserSession[]; total: number }>('/sessions')
    },

    createSession: async (config: CreateSessionRequest): Promise<BrowserSession> => {
      return this.request<BrowserSession>('/sessions', {
        method: 'POST',
        body: JSON.stringify(config),
      })
    },

    getSession: async (sessionId: string): Promise<BrowserSession> => {
      return this.request<BrowserSession>(`/sessions/${sessionId}`)
    },

    stopSession: async (sessionId: string): Promise<void> => {
      await this.request<void>(`/sessions/${sessionId}`, {
        method: 'DELETE',
      })
    }
  }

  // Utility methods
  async healthCheck(): Promise<{ status: string }> {
    return this.request<{ status: string }>('/health')
  }
}

// Export singleton instance
export const anchorBrowserApi = new AnchorBrowserApiClient()

// Export class for custom instances
export { AnchorBrowserApiClient }
